import {logger} from '../../../../logger';
import {makeAutoObservable, runInAction} from 'mobx';
import {SpecialStoreFilter} from '../../../storepage.def';
import {validateAndPrepareStoreResults} from '../../../store-results/validate-and-prepare-store-results';
import axios from 'axios';
import storePageDataSource from '../../../storepage-datasource';
import {getAntiForgeryToken, Portal} from '../../../../util';
import {STORE_PAGE_SIZE} from '../../product-results/format-product-result-summary';
import {isAntiforgeryFailure, REFRESH_PAGE_MSG} from '../../../../global/antiforgery';
import {isRequestCanceled} from '../../../../global/request-error';
import {PRODUCTS_LOAD_ERROR} from '../../product-list-status/store-products-status';
import {GRADUATION_YEAR_ERROR, isValidGraduationYearFilter} from '../graduation-year/is-valid-graduation-year';
import {parseStoreSelection, prefilterFrameTypes, serializeStoreSearch, splitAppliedList, storeSearchChanged, type StoreFrameType, type StoreSelection, type StoreSelectionParseOptions} from '../url/store-selection';
import {syncStoreCanonical} from '../url/store-canonical';

/**
 * A search that never answers used to leave the school field read-only. Axios reports that stall as
 * ECONNABORTED (XHR) or ETIMEDOUT (fetch), which reaches the existing Retry message. Aborting a
 * superseded search is ERR_CANCELED and must not.
 */
export const STORE_SEARCH_TIMEOUT_MS = 30000;

/** The search whose completion may unlock the school field. Kept off the store so MobX does not proxy AbortController. */
const activeStoreSearch = new WeakMap<object, AbortController>();

declare var __special_id: number;
declare var __fastShipIds: number[];
declare var __InstitutionID: number;
declare var __mouldingOptions: string[] | undefined;
declare var __matOptions: string[] | undefined;
declare var __prefilter: number;

export enum FrameType {
    Tassel = 'tassel',
    Diploma = 'diploma',
    Certificate = 'certificate',
}

export class FiltersStore {
    public filters: SpecialStoreFilter = {
        specialId: __special_id,
        finish: '',
        matcombo: '',
        priceMin: 0,
        priceMax: 500,
        inst: '',
        sort: '',
        pageNumber: 1,
        InstitutionID: __InstitutionID,
        frameType: [],
        graduationYear: '',
    };
    public loading = false;
    public requestInFlight = false;
    public hasLoadedSuccessfully = false;
    public errorMessage = '';
    public selectedFinish: string[] = [];
    public selectedMatCombo: string[] = [];
    public selectedFrameType: FrameType[] = [];
    public selectedGraduationYear: string = '';
    public schoolSearchDraft = '';
    public schoolSearchError = '';
    public graduationYearError: string = '';
    public debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    private schoolSearchDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
    private schoolSearchShouldFlushPendingFilters = false;
    private requestVersion = 0;
    private historyToken = 0;
    /** `replace` is only for the first school refresh from the URL, so that refresh does not add a history entry. */
    private nextHistoryMode: 'push' | 'replace' = 'push';
    private lastFilterKey: keyof SpecialStoreFilter = 'pageNumber';
    private isBestSellersMode = false;
    private priceRangeListener: ((min: number, max: number) => void) | null = null;
    /** Frame types locked by the server `__prefilter`. Selections restored from the URL stay removable. */
    private baselineFrameType: FrameType[] = [];

    constructor() {
        makeAutoObservable(this);
        this.applyFromCurrentLocation();
        this.baselineFrameType = this.toFrameTypes(prefilterFrameTypes(this.prefilterValue()));

        // A school in the URL uses the same 300ms refresh as typing, so a fast edit can replace it first.
        // That refresh is not a new filter change, so the page from the same URL stays.
        if (this.schoolSearchDraft.length > 1) {
            const pageNumber = this.filters.pageNumber;
            this.nextHistoryMode = 'replace';
            void this.updateFilter('inst', this.schoolSearchDraft);
            this.filters.pageNumber = pageNumber;
        }
    }

    /** The price slider mirrors URL restores without sending another filter change. */
    registerPriceRangeListener(listener: (min: number, max: number) => void) {
        this.priceRangeListener = listener;
    }

    applyFromCurrentLocation() {
        let selection: StoreSelection;
        try {
            const search = typeof window !== 'undefined' && window.location?.search ? window.location.search : '';
            selection = parseStoreSelection(new URLSearchParams(search), this.parseOptions());
        } catch (err) {
            logger.error(err);
            selection = parseStoreSelection(new URLSearchParams(), this.parseOptions());
        }
        this.applySelection(selection);
    }

    public getBaselineFrameTypes(): FrameType[] {
        return [...this.baselineFrameType];
    }

    /** Links use the active selection, including filters whose refresh has not written history yet. */
    get paginationHrefBuilder(): (pageNumber: number) => string {
        const selection = this.currentSelection();
        const baseline = {prefilter: this.prefilterValue()};
        return pageNumber => {
            const location = typeof window !== 'undefined' ? window.location : undefined;
            const search = serializeStoreSearch(location?.search || '', {...selection, pageNumber}, baseline);
            return `${location?.pathname || '/'}${search}${location?.hash || ''}`;
        };
    }

    /**
     * First paint. A `?s=` link already scheduled its refresh, and that refresh
     * recovers a page past the end of the results. Otherwise fetch now.
     */
    async loadInitialResults() {
        if (this.debounceTimeout || this.schoolSearchDebounceTimeout) {
            return;
        }

        await this.restoreResultsFromCurrentFilters();
    }

    /**
     * Browser Back/Forward. Any in-flight refresh is ignored so it cannot put
     * the previous page back. A page the result set no longer has is corrected
     * with replaceState, which does not add a history entry.
     */
    async restoreFromLocation() {
        this.cancelPendingRefresh();
        this.applyFromCurrentLocation();
        this.loading = true;
        this.errorMessage = '';
        await this.restoreResultsFromCurrentFilters();
    }

    async getData(): Promise<number | null> {
        const requestVersion = ++this.requestVersion;
        this.loading = true;
        this.errorMessage = '';

        // Own the school-field lock before aborting, so a superseded search cannot clear or keep it.
        const controller = new AbortController();
        const superseded = activeStoreSearch.get(this);
        activeStoreSearch.set(this, controller);
        this.requestInFlight = true;
        superseded?.abort();

        try {
            if (__InstitutionID) {
                this.filters.InstitutionID = __InstitutionID;
            }

            const config = {
                headers: {
                    RequestVerificationToken: getAntiForgeryToken(),
                },
                signal: controller.signal,
                timeout: STORE_SEARCH_TIMEOUT_MS,
            };

            const filters = {...this.filters, frameType: [...this.filters.frameType], bestSellers: this.isBestSellersMode};
            const response = await axios.post('/api/special_store', filters, config);

            if (requestVersion !== this.requestVersion) {
                return null;
            }

            // Check if the response contains an Elasticsearch error
            if (response.data && response.data.error) {
                logger.error('Elasticsearch Error:', response.data);
                this.markProductLoadFailed();
                return null;
            }

            // A copy, stamped before either field is published. A bad hit leaves the previous page in place.
            const products = validateAndPrepareStoreResults(response.data, __fastShipIds);
            if (!products) {
                logger.error('Unexpected store API response:', response.data);
                this.markProductLoadFailed();
                return null;
            }

            const acceptedPageNumber = this.filters.pageNumber;
            runInAction(() => {
                storePageDataSource.products = products;
                storePageDataSource.acceptedPageNumber = acceptedPageNumber;
            });

            storePageDataSource.getExtra();

            this.hasLoadedSuccessfully = true;
            this.errorMessage = '';
            return requestVersion;
        } catch (err) {
            // axios rejects every non-2xx answer, so every failed request lands here.
            // Cancellation is the superseded search being dropped. A timeout is not a cancellation.
            if (isRequestCanceled(err) || requestVersion !== this.requestVersion) {
                return null;
            }
            logger.error(err);
            this.markProductLoadFailed();
            if (isAntiforgeryFailure(err)) {
                this.errorMessage = REFRESH_PAGE_MSG;
            }
            return null;
        } finally {
            if (activeStoreSearch.get(this) === controller) {
                activeStoreSearch.delete(this);
                this.requestInFlight = false;
            }
            if (requestVersion === this.requestVersion) {
                this.loading = false;
            }
        }
    }

    getMatOptions() {
        let options: string[] | undefined = typeof __matOptions !== 'undefined' && Array.isArray(__matOptions) ? __matOptions : undefined;

        if (!options || options.length === 0) {
            if (!storePageDataSource.products?.hits?.hits) {
                storePageDataSource.matOptions = [''];
                return;
            }

            options = storePageDataSource.products.hits.hits
                .filter(x => x._source && x._source.MatComboName)
                .map(p => p._source.MatComboName)
                .filter(option => option && option.trim() !== '');
        }

        storePageDataSource.matOptions = ['', ...new Set(options)];
    }

    getMouldingOptions() {
        let options: string[] | undefined = typeof __mouldingOptions !== 'undefined' && Array.isArray(__mouldingOptions) ? __mouldingOptions : undefined;

        if (!options || options.length === 0) {
            if (!storePageDataSource.products?.hits?.hits) {
                storePageDataSource.mouldingOptions = [''];
                return;
            }

            options = storePageDataSource.products.hits.hits
                .filter(x => x._source && x._source.MouldingFinish)
                .map(p => p._source.MouldingFinish)
                .filter(option => option && option.trim() !== '');
        }

        storePageDataSource.mouldingOptions = ['', ...new Set(options)];
    }

    async updateFilter<Key extends keyof SpecialStoreFilter>(key: Key, value: SpecialStoreFilter[Key]) {
        const historyMode = this.nextHistoryMode;
        this.nextHistoryMode = 'push';
        // A committed search or filter opens on page 1. A page click passes pageNumber and keeps that page,
        // and it leaves a school draft waiting so that draft can still open on page 1.
        if (key !== 'pageNumber') {
            this.absorbPendingSchoolDraft();
            this.filters.pageNumber = 1;
        }

        this.filters[key] = value;
        this.lastFilterKey = key;
        // Invalidate older responses as soon as the selection changes, including during debounce.
        this.requestVersion++;
        this.loading = true;
        this.errorMessage = '';

        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }

        const token = ++this.historyToken;
        const timeout = key === 'inst' || key === 'priceMin' || key === 'priceMax' || key === 'graduationYear' ? 300 : 0;

        this.debounceTimeout = setTimeout(() => {
            this.debounceTimeout = null;
            void this.applyFilterAndHandlePagination(key, token, historyMode);
        }, timeout);
    }

    async retry() {
        return this.applyFilterAndHandlePagination(this.lastFilterKey);
    }

    /** Failed request for the current version. Clears the server ItemList only when no accepted page exists yet. */
    markProductLoadFailed() {
        this.errorMessage = PRODUCTS_LOAD_ERROR;
        storePageDataSource.clearStoreSchemaWhenNoAcceptedResults();
    }

    async applyFilterAndHandlePagination<Key extends keyof SpecialStoreFilter>(key: Key, historyToken?: number, historyMode: 'push' | 'replace' = 'push') {
        const writesHistory = historyToken !== undefined && historyToken === this.historyToken;
        if (historyToken !== undefined && !writesHistory) {
            return;
        }

        if (writesHistory && historyMode === 'push') {
            this.writeSelection('push');
        }

        let appliedVersion = await this.getData();

        // A page click stays where the customer put it. Other changes walk off an empty page.
        while (key !== 'pageNumber' && this.isPagePastResults(appliedVersion)) {
            this.stepToEarlierPage();
            appliedVersion = await this.getData();
        }

        // Page walk can change the page after the entry was added. Replace that
        // entry so Back stays one step, and skip it when a newer change won.
        if (writesHistory && historyToken === this.historyToken) {
            this.writeSelection('replace');
        }
        this.syncAcceptedSelectionUrl(appliedVersion);
    }

    async finishChanged(checked: boolean, value: string) {
        if (checked) {
            if (value === '') {
                // "All Mouldings" selected - clear all other selections
                this.selectedFinish = [''];
            } else {
                // Remove "All Mouldings" if it was selected and add the specific selection
                this.selectedFinish = this.selectedFinish.filter(f => f !== '');
                if (!this.selectedFinish.includes(value)) {
                    this.selectedFinish.push(value);
                }
            }
        } else {
            this.selectedFinish = this.selectedFinish.filter(f => f !== value);
        }

        await this.updateFilter('finish', this.selectedFinish.join(','));
    }

    async matComboChanged(checked: boolean, value: string) {
        if (checked) {
            if (value === '') {
                // "All Mats" selected - clear all other selections
                this.selectedMatCombo = [''];
            } else {
                // Remove "All Mats" if it was selected and add the specific selection
                this.selectedMatCombo = this.selectedMatCombo.filter(m => m !== '');
                if (!this.selectedMatCombo.includes(value)) {
                    this.selectedMatCombo.push(value);
                }
            }
        } else {
            this.selectedMatCombo = this.selectedMatCombo.filter(m => m !== value);
        }

        await this.updateFilter('matcombo', this.selectedMatCombo.join(','));
    }

    async frameTypeChanged(checked: boolean, type: FrameType) {
        if (checked) {
            if (!this.selectedFrameType.includes(type)) {
                this.selectedFrameType.push(type);
            }
        } else {
            this.selectedFrameType = this.selectedFrameType.filter(f => f !== type);
        }

        await this.updateFilter('frameType', this.selectedFrameType);
    }

    async graduationYearChanged(value: string) {
        const isValid = isValidGraduationYearFilter(value);
        this.selectedGraduationYear = value;
        this.graduationYearError = isValid ? '' : GRADUATION_YEAR_ERROR;
        if (isValid) {
            await this.updateFilter('graduationYear', value);
        }
    }

    private setSchoolSearchDraft(draft: string) {
        this.schoolSearchDraft = draft;
        this.schoolSearchError = draft.length === 1 ? 'Enter at least 2 characters.' : '';
    }

    private cancelSchoolSearchDebounce() {
        if (this.schoolSearchDebounceTimeout) {
            clearTimeout(this.schoolSearchDebounceTimeout);
            this.schoolSearchDebounceTimeout = null;
        }

        this.schoolSearchShouldFlushPendingFilters = false;
    }

    /**
     * A price or sort change during school typing should send one request.
     * Commit a ready school draft and drop its timer so the filter debounce owns the refresh.
     */
    private absorbPendingSchoolDraft() {
        if (!this.schoolSearchDebounceTimeout) {
            return;
        }

        const draft = this.schoolSearchDraft;
        const clearingOneCharacter = draft.length === 1 && this.schoolSearchShouldFlushPendingFilters;
        const nextInst = draft.length > 1 ? draft : draft.length === 0 || clearingOneCharacter ? '' : null;
        this.cancelSchoolSearchDebounce();
        if (nextInst !== null && nextInst !== this.filters.inst) {
            this.filters.inst = nextInst;
        }
    }

    async onSchoolSearch(draft: string) {
        this.setSchoolSearchDraft(draft);

        if (this.schoolSearchDebounceTimeout) {
            clearTimeout(this.schoolSearchDebounceTimeout);
            this.schoolSearchDebounceTimeout = null;
        }

        const nextInst = this.schoolSearchDraft.length > 1 ? this.schoolSearchDraft : '';
        const schoolFilterChanged = nextInst !== this.filters.inst;

        if (schoolFilterChanged) {
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
                this.debounceTimeout = null;
            }

            this.requestVersion++;
            this.schoolSearchShouldFlushPendingFilters = true;
        }

        this.schoolSearchDebounceTimeout = setTimeout(() => {
            this.schoolSearchDebounceTimeout = null;
            const appliedInst = this.schoolSearchDraft.length > 1 ? this.schoolSearchDraft : '';

            if (appliedInst === this.filters.inst) {
                const shouldFlushPendingFilters = this.schoolSearchShouldFlushPendingFilters && !this.debounceTimeout && (this.loading || this.requestInFlight);
                this.schoolSearchShouldFlushPendingFilters = false;

                if (shouldFlushPendingFilters) {
                    const token = ++this.historyToken;
                    void this.applyFilterAndHandlePagination(this.lastFilterKey, token);
                }

                return;
            }

            this.schoolSearchShouldFlushPendingFilters = false;
            // The applied school changed, including a clear. The page click that follows can still move off page 1.
            this.filters.pageNumber = 1;
            this.filters.inst = appliedInst;
            this.lastFilterKey = 'inst';
            this.requestVersion++;
            this.loading = true;
            this.errorMessage = '';
            const token = ++this.historyToken;
            void this.applyFilterAndHandlePagination('inst', token);
        }, 300);
    }

    async clearSchoolSelection(): Promise<void> {
        this.cancelSchoolSearchDebounce();
        this.setSchoolSearchDraft('');
        if (this.filters.inst !== '') {
            await this.updateFilter('inst', '');
        }
    }

    async clearGraduationYearSelection(): Promise<void> {
        this.selectedGraduationYear = '';
        this.graduationYearError = '';
        if (this.filters.graduationYear !== '') {
            await this.updateFilter('graduationYear', '');
        }
    }

    async applyPriceFilter(priceMin: number, priceMax: number): Promise<void> {
        if (this.filters.priceMin === priceMin && this.filters.priceMax === priceMax) {
            return;
        }

        this.filters.priceMin = priceMin;
        await this.updateFilter('priceMax', priceMax);
    }

    async removeUserFrameType(type: FrameType): Promise<void> {
        if (this.baselineFrameType.includes(type)) {
            return;
        }

        await this.frameTypeChanged(false, type);
    }

    /**
     * Drops customer narrowing in one pass. Store id, institution id, best-sellers mode,
     * the prefilter frame types, and the current sort are left as they are.
     * `syncPriceRange` runs inside this update so the slider matches before the refresh.
     */
    async clearUserFilters(priceMin: number, priceMax: number, syncPriceRange?: () => void): Promise<number | null> {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = null;
        }

        this.cancelSchoolSearchDebounce();

        // Invalidate a response or debounce that started before this reset, then refresh once.
        this.requestVersion++;
        const historyToken = ++this.historyToken;
        syncPriceRange?.();

        this.selectedFinish = [];
        this.selectedMatCombo = [];
        this.selectedFrameType = [...this.baselineFrameType];
        this.selectedGraduationYear = '';
        this.graduationYearError = '';
        this.setSchoolSearchDraft('');

        this.filters.finish = '';
        this.filters.matcombo = '';
        this.filters.priceMin = priceMin;
        this.filters.priceMax = priceMax;
        this.filters.inst = '';
        this.filters.frameType = [...this.baselineFrameType];
        this.filters.graduationYear = '';
        this.filters.pageNumber = 1;
        this.lastFilterKey = 'pageNumber';
        this.loading = true;
        this.errorMessage = '';

        const appliedVersion = await this.getData();
        if (appliedVersion === this.requestVersion && historyToken === this.historyToken) {
            this.writeSelection('push');
            this.syncAcceptedSelectionUrl(appliedVersion);
        }

        return appliedVersion;
    }

    private applySelection(selection: StoreSelection) {
        this.filters.specialId = __special_id;
        this.filters.InstitutionID = __InstitutionID;
        this.setSchoolSearchDraft(selection.inst);
        this.filters.inst = selection.inst.length > 1 ? selection.inst : '';
        this.filters.sort = selection.sort;
        this.filters.pageNumber = selection.pageNumber;
        this.filters.priceMin = selection.priceMin;
        this.filters.priceMax = selection.priceMax;
        this.filters.finish = selection.finish.join(',');
        this.selectedFinish = [...selection.finish];
        this.filters.matcombo = selection.matcombo.join(',');
        this.selectedMatCombo = [...selection.matcombo];
        this.filters.graduationYear = selection.graduationYear;
        this.selectedGraduationYear = selection.graduationYear;
        this.graduationYearError = '';
        this.filters.frameType = [...selection.frameType];
        this.selectedFrameType = selection.frameType.map(type => type as FrameType);
        this.isBestSellersMode = selection.bestSellers;
        this.applyPrefilterLock();
        this.dropFiltersHiddenOnThisPage();
        this.priceRangeListener?.(this.filters.priceMin, this.filters.priceMax);
    }

    /**
     * A shared link can name a filter this page does not show. Drop it and
     * replace the current URL so the address bar matches the visible controls.
     */
    private dropFiltersHiddenOnThisPage() {
        let dropped = false;

        if (!this.graduationYearControlIsShown() && (this.filters.graduationYear !== '' || this.selectedGraduationYear !== '')) {
            this.filters.graduationYear = '';
            this.selectedGraduationYear = '';
            this.graduationYearError = '';
            dropped = true;
        }

        if (!this.matControlIsShown() && (this.filters.matcombo !== '' || this.selectedMatCombo.length > 0)) {
            this.filters.matcombo = '';
            this.selectedMatCombo = [];
            dropped = true;
        }

        if (dropped) {
            this.writeSelection('replace');
        }
    }

    private graduationYearControlIsShown(): boolean {
        return Portal?.PortalID === 3;
    }

    private matControlIsShown(): boolean {
        const path = typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : '';
        return !path.includes('best-sellers');
    }

    /** `__prefilter` wins over the URL so a shared link cannot leave the store's category. */
    private applyPrefilterLock() {
        const prefilter = this.prefilterValue();
        if (prefilter === 1) {
            this.isBestSellersMode = true;
        }

        const locked = this.toFrameTypes(prefilterFrameTypes(prefilter));
        if (locked.length > 0) {
            this.selectedFrameType = locked;
            this.filters.frameType = [...locked];
        }
    }

    private cancelPendingRefresh() {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = null;
        }
        this.cancelSchoolSearchDebounce();
        this.historyToken++;
        this.requestVersion++;
    }

    /** Fetch the current filters, then leave a page that came back empty while products still match. */
    private async restoreResultsFromCurrentFilters() {
        const appliedVersion = await this.recoverOutOfRangePage();
        this.syncAcceptedSelectionUrl(appliedVersion);
    }

    /** Pending, failed, and superseded requests keep the canonical of the last accepted results. */
    private syncAcceptedSelectionUrl(appliedVersion: number | null) {
        if (appliedVersion !== this.requestVersion || this.debounceTimeout || this.schoolSearchDebounceTimeout) return;
        this.writeSelection('replace');
        if (typeof window !== 'undefined' && window.location) syncStoreCanonical(window.location.search || '');
    }

    private async recoverOutOfRangePage(): Promise<number | null> {
        let appliedVersion = await this.getData();
        while (this.isPagePastResults(appliedVersion)) {
            this.stepToEarlierPage();
            appliedVersion = await this.getData();
        }

        return appliedVersion;
    }

    private isPagePastResults(appliedVersion: number | null): boolean {
        return appliedVersion === this.requestVersion && !this.debounceTimeout && !this.schoolSearchDebounceTimeout && this.filters.pageNumber > 1 && storePageDataSource.products.hits.hits.length === 0;
    }

    /**
     * An empty page whose total still names products jumps to the last page that can hold them.
     * A confirmed total of 0 has no earlier populated page, so the next fetch is page 1.
     */
    private stepToEarlierPage() {
        const total = storePageDataSource.products.hits.total?.value ?? 0;
        if (total > 0) {
            const lastPage = Math.max(1, Math.ceil(total / STORE_PAGE_SIZE));
            this.filters.pageNumber = Math.min(this.filters.pageNumber - 1, lastPage);
            return;
        }

        this.filters.pageNumber = 1;
    }

    private writeSelection(mode: 'push' | 'replace') {
        try {
            if (typeof window === 'undefined' || !window.history || typeof window.history.pushState !== 'function' || !window.location) {
                return;
            }

            const nextSearch = serializeStoreSearch(window.location.search || '', this.currentSelection(), {prefilter: this.prefilterValue()});
            if (!storeSearchChanged(window.location.search || '', nextSearch)) {
                return;
            }

            const pathname = window.location.pathname || '/';
            const hash = window.location.hash || '';
            const url = `${pathname}${nextSearch}${hash}`;
            if (mode === 'push') {
                window.history.pushState(null, '', url);
            } else {
                window.history.replaceState(null, '', url);
            }
        } catch (err) {
            logger.error(err);
        }
    }

    private currentSelection(): StoreSelection {
        return {
            inst: this.filters.inst,
            sort: this.filters.sort,
            pageNumber: this.filters.pageNumber,
            priceMin: this.filters.priceMin,
            priceMax: this.filters.priceMax,
            finish: splitAppliedList(this.filters.finish),
            matcombo: splitAppliedList(this.filters.matcombo),
            frameType: this.filters.frameType.filter(isStoreFrameTypeValue),
            bestSellers: this.isBestSellersMode,
            graduationYear: this.filters.graduationYear,
        };
    }

    private parseOptions(): StoreSelectionParseOptions {
        return {
            matOptions: typeof __matOptions !== 'undefined' && Array.isArray(__matOptions) ? __matOptions : undefined,
            mouldingOptions: typeof __mouldingOptions !== 'undefined' && Array.isArray(__mouldingOptions) ? __mouldingOptions : undefined,
            portalId: Portal?.PortalID,
        };
    }

    private prefilterValue(): number {
        return typeof __prefilter !== 'undefined' ? __prefilter : 0;
    }

    private toFrameTypes(values: StoreFrameType[]): FrameType[] {
        return values.map(type => type as FrameType);
    }
}

function isStoreFrameTypeValue(value: string): value is StoreFrameType {
    return value === 'tassel' || value === 'diploma' || value === 'certificate';
}

const filtersStore = new FiltersStore();

export default filtersStore;
